import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Paths } from "./Paths";
import { Spin } from "antd";
import { PATH } from "Helpers/ats.constants";

// Lazy loaded pages
const LoginPage = lazy(() => import("pages/Login"));
const TermsOfUsePage = lazy(() => import("pages/Legal/TermsOfUse"));
const PrivacyPolicyPage = lazy(() => import("pages/Legal/PrivacyPolicy"));
const DashboardPage = lazy(() => import("pages/Dashboard"));
const OpenSalesOrdersPage = lazy(() => import("pages/OpenSalesOrders"));
const SODetailViewPage = lazy(() => import("pages/OpenSalesOrders/SODetailView"));
const InvoicesListPage = lazy(() => import("pages/Invoices/InvoicesList"));
const InvoiceViewPage = lazy(() => import("pages/Invoices/InvoiceView"));
const CreateDirectInvoicePage = lazy(() => import("pages/Invoices/CreateDirectInvoice"));
const CreateInvoiceFromSOPage = lazy(() => import("pages/Invoices/CreateInvoiceFromSO"));
const EditDraftInvoicePage = lazy(() => import("pages/Invoices/EditDraftInvoice"));
const UsersListPage = lazy(() => import("pages/Users/UsersList"));
const AddEditUserPage = lazy(() => import("pages/Users/AddEditUser"));
const RolesListPage = lazy(() => import("pages/Roles/RolesList"));
const AddEditRolePage = lazy(() => import("pages/Roles/AddEditRole"));
const AuditLogsListPage = lazy(() => import("pages/AuditLogs/AuditLogsList"));
const DepotsListPage = lazy(() => import("pages/Depots/DepotsList"));
const AddEditDepotPage = lazy(() => import("pages/Depots/AddEditDepot"));

// Retail — Depot counter sales (AB / RB / DW / Normal)
const SaleBillsListPage = lazy(() => import("pages/SaleBills/SaleBillsList"));
const CreateSaleBillPage = lazy(() => import("pages/SaleBills/CreateSaleBill"));
// Path A: literal-port POS Sale Bill GUI (mounted in parallel)
// Wrapper, not the raw mirror: PosSaleBillScreen applies POS's antd design
// tokens to this route only, so the screen matches POS visually while
// pos-mirror/SalesBill.js stays byte-identical to POS (it is overwritten by
// scripts/sync-pos-mirror.sh).
const CreateSaleBillPosPage = lazy(() => import("pages/SaleBills/PosSaleBillScreen"));
const ViewSaleBillPage = lazy(() => import("pages/SaleBills/ViewSaleBill"));
const SaleReturnsListPage = lazy(() => import("pages/SaleBills/SaleReturnsList"));
const CreateSaleReturnPage = lazy(() => import("pages/SaleBills/CreateSaleReturn"));
const ViewSaleReturnPage = lazy(() => import("pages/SaleBills/ViewSaleReturn"));

// WMS Sprint 1 - Masters
const CompaniesListPage = lazy(() => import("pages/Companies/CompaniesList"));
const AddEditCompanyPage = lazy(() => import("pages/Companies/AddEditCompany"));
const ZonesListPage = lazy(() => import("pages/WarehouseZones/ZonesList"));
const AddEditZonePage = lazy(() => import("pages/WarehouseZones/AddEditZone"));
const BinsListPage = lazy(() => import("pages/WarehouseBins/BinsList"));
const AddEditBinPage = lazy(() => import("pages/WarehouseBins/AddEditBin"));
const CustomersListPage = lazy(() => import("pages/Customers/CustomersList"));
const AddEditCustomerPage = lazy(() => import("pages/Customers/AddEditCustomer"));
const SuppliersListPage = lazy(() => import("pages/Suppliers/SuppliersList"));
const AddEditSupplierPage = lazy(() => import("pages/Suppliers/AddEditSupplier"));
const NumberSeriesListPage = lazy(() => import("pages/NumberSeries/NumberSeriesList"));
const AddEditNumberSeriesPage = lazy(() => import("pages/NumberSeries/AddEditNumberSeries"));
const ConfigurationsListPage = lazy(() => import("pages/Configurations/ConfigurationsList"));
const AddEditConfigurationPage = lazy(() => import("pages/Configurations/AddEditConfiguration"));
const MinInvoiceValuesPage = lazy(() => import("pages/Configurations/MinInvoiceValues"));
const StoreBrandRestrictionsPage = lazy(() => import("pages/Configurations/StoreBrandRestrictions"));
const StoreBlockListPage = lazy(() => import("pages/Configurations/StoreBlockList"));
const SapLogsListPage = lazy(() => import("pages/SapLogs/SapLogsList"));
const AnnouncementsListPage = lazy(() => import("pages/Announcements/AnnouncementsList"));

// WMS Sprint 2 - Inventory Operations
const PurchaseOrdersListPage = lazy(() => import("pages/PurchaseOrders/PurchaseOrdersList"));
const CreateEditPOPage = lazy(() => import("pages/PurchaseOrders/CreateEditPO"));
const POViewPage = lazy(() => import("pages/PurchaseOrders/POView"));
const GRNListPage = lazy(() => import("pages/GRN/GRNList"));
const CreateGRNPage = lazy(() => import("pages/GRN/CreateGRN"));
const GRNViewPage = lazy(() => import("pages/GRN/GRNView"));
const StoReceiptPage = lazy(() => import("pages/GRN/StoReceipt"));
const PoReceiptPage = lazy(() => import("pages/GRN/PoReceipt"));
const BatchesListPage = lazy(() => import("pages/Batches/BatchesList"));
const StockOnHandPage = lazy(() => import("pages/Stock/StockOnHand"));
const StockMovementsPage = lazy(() => import("pages/Stock/StockMovements"));
const StockAdjustmentPage = lazy(() => import("pages/Stock/StockAdjustment"));
const OpeningStockPage = lazy(() => import("pages/OpeningStock/OpeningStockList"));
const StockRegisterPage = lazy(() => import("pages/Stock/StockRegister"));
const SalesRegisterPage = lazy(() => import("pages/Stock/SalesRegister"));
const PurchaseRegisterPage = lazy(() => import("pages/Stock/PurchaseRegister"));
const ProductWiseSalesReportPage = lazy(() => import("pages/Stock/ProductWiseSalesReport"));

// Phase 2 - Sales Orders
const SalesOrdersListPage = lazy(() => import("pages/SalesOrders/SalesOrdersList"));
const CreateEditSOPage = lazy(() => import("pages/SalesOrders/CreateEditSO"));
const SOViewPage = lazy(() => import("pages/SalesOrders/SOView"));

// Phase 2 - Picking
const PickListsListPage = lazy(() => import("pages/Picking/PickListsList"));
const CreatePickListPage = lazy(() => import("pages/Picking/CreatePickList"));
const PickListViewPage = lazy(() => import("pages/Picking/PickListView"));

// Phase 2 - Dispatch
const DispatchListPage = lazy(() => import("pages/Dispatch/DispatchList"));
const CreateDispatchPage = lazy(() => import("pages/Dispatch/CreateDispatch"));
const DispatchViewPage = lazy(() => import("pages/Dispatch/DispatchView"));

// Phase 2 - WMS Invoices
const WmsInvoicesListPage = lazy(() => import("pages/WmsInvoices/WmsInvoicesList"));
const WmsInvoiceViewPage = lazy(() => import("pages/WmsInvoices/WmsInvoiceView"));

// Phase 2 - Credit Notes
const CreditNotesListPage = lazy(() => import("pages/CreditNotes/CreditNotesList"));
const CreateCreditNotePage = lazy(() => import("pages/CreditNotes/CreateCreditNote"));
const CreditNoteViewPage = lazy(() => import("pages/CreditNotes/CreditNoteView"));

// Phase 2 - Debit Notes
const DebitNotesListPage = lazy(() => import("pages/DebitNotes/DebitNotesList"));
const CreateDebitNotePage = lazy(() => import("pages/DebitNotes/CreateDebitNote"));
const DebitNoteViewPage = lazy(() => import("pages/DebitNotes/DebitNoteView"));

// Phase 2 - Payments
const PaymentsListPage = lazy(() => import("pages/Payments/PaymentsList"));
const CreatePaymentPage = lazy(() => import("pages/Payments/CreatePayment"));
const PaymentViewPage = lazy(() => import("pages/Payments/PaymentView"));

// Phase 2 - Sales Returns
const SalesReturnsListPage = lazy(() => import("pages/SalesReturns/SalesReturnsList"));
const CreateSalesReturnPage = lazy(() => import("pages/SalesReturns/CreateSalesReturn"));
const SalesReturnViewPage = lazy(() => import("pages/SalesReturns/SalesReturnView"));

// Depo Returns (walk-in / POS-originated invoice returns)
const DepoReturnsListPage = lazy(() => import("pages/DepoReturns/DepoReturnsList"));
const CreateEditDepoReturnPage = lazy(() => import("pages/DepoReturns/CreateEditDepoReturn"));
const DepoReturnViewPage = lazy(() => import("pages/DepoReturns/DepoReturnView"));
const PosReturnSOsListPage = lazy(() => import("pages/DepoReturns/PosReturnSOsList"));
const PosReturnSOImportPage = lazy(() => import("pages/DepoReturns/PosReturnSOImport"));

// Phase 3 - Putaway
const PutawayTasksListPage = lazy(() => import("pages/Putaway/PutawayTasksList"));
const PutawayViewPage = lazy(() => import("pages/Putaway/PutawayView"));

// Phase 3 - Shelf Transfers
const ShelfTransfersListPage = lazy(() => import("pages/ShelfTransfers/ShelfTransfersList"));
const CreateShelfTransferPage = lazy(() => import("pages/ShelfTransfers/CreateShelfTransfer"));
const ShelfTransferViewPage = lazy(() => import("pages/ShelfTransfers/ShelfTransferView"));

// Phase 3 - Cycle Counts
const CycleCountsListPage = lazy(() => import("pages/CycleCounts/CycleCountsList"));
const CreateCycleCountPage = lazy(() => import("pages/CycleCounts/CreateCycleCount"));
const CycleCountViewPage = lazy(() => import("pages/CycleCounts/CycleCountView"));

// Purchase Requests
const PurchaseRequestsListPage = lazy(() => import("pages/PurchaseRequests/PurchaseRequestsList"));
const CreatePurchaseRequestPage = lazy(() => import("pages/PurchaseRequests/CreatePurchaseRequest"));
const EditPurchaseRequestPage = lazy(() => import("pages/PurchaseRequests/EditPurchaseRequest"));
const PRViewPage = lazy(() => import("pages/PurchaseRequests/PRView"));
const DepotPRMappingsPage = lazy(() => import("pages/PurchaseRequests/DepotPRMappings"));

// Inter-Depot Purchase Orders
const InterDepotPOsListPage = lazy(() => import("pages/InterDepotPO/InterDepotPOList"));
const CreatePOFromPRPage = lazy(() => import("pages/InterDepotPO/CreatePOFromPR"));
const InterDepotPOViewPage = lazy(() => import("pages/InterDepotPO/InterDepotPOView"));

// Phase 3 - Stock Transfers
const StockTransfersListPage = lazy(() => import("pages/StockTransfers/StockTransfersList"));
const CreateStockTransferPage = lazy(() => import("pages/StockTransfers/CreateStockTransfer"));
const StockTransferViewPage = lazy(() => import("pages/StockTransfers/StockTransferView"));
const StockTransferReceivePage = lazy(() => import("pages/StockTransfers/StockTransferReceive"));

// Phase 3 - Stock Levels & Expiry
const StockLevelsPage = lazy(() => import("pages/StockLevels/StockLevels"));
const StockAlertsPage = lazy(() => import("pages/StockLevels/StockAlerts"));
const ExpiryDashboardPage = lazy(() => import("pages/ExpiryManagement/ExpiryDashboard"));
const ExpirySettingsPage = lazy(() => import("pages/ExpiryManagement/ExpirySettings"));

const PageLoader = () => (
  <div style={{ textAlign: "center", padding: 80 }}>
    <Spin size="large" />
  </div>
);

export default function AppRoutes({ loggedIn }) {
  if (!loggedIn) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path={Paths.termsOfUse} element={<TermsOfUsePage />} />
          <Route path={Paths.privacyPolicy} element={<PrivacyPolicyPage />} />
          <Route path="*" element={<LoginPage />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Dashboard */}
        <Route path={Paths.dashboard} element={<DashboardPage />} />

        {/* Public legal pages (also linked from the login footer) */}
        <Route path={Paths.termsOfUse} element={<TermsOfUsePage />} />
        <Route path={Paths.privacyPolicy} element={<PrivacyPolicyPage />} />

        {/* Open Sales Orders */}
        <Route path={Paths.openSalesOrders} element={<OpenSalesOrdersPage />} />
        <Route path={Paths.viewOpenSO} element={<SODetailViewPage />} />

        {/* Invoices */}
        <Route path={Paths.invoices} element={<InvoicesListPage />} />
        <Route path={Paths.createInvoice} element={<CreateInvoiceFromSOPage />} />
        <Route path={Paths.createDirectInvoice} element={<CreateDirectInvoicePage />} />
        <Route path={`/${PATH}/invoices/view/:invoice_no`} element={<InvoiceViewPage />} />
        {/* Drafts have no invoice_no — view by invoice_id */}
        <Route path={`/${PATH}/invoices/view-draft/:invoice_id`} element={<InvoiceViewPage />} />
        <Route path={Paths.editInvoice} element={<EditDraftInvoicePage />} />
        {/* Drafts have no invoice_no — edit by invoice_id */}
        <Route path={`/${PATH}/invoices/edit-draft/:invoice_id`} element={<EditDraftInvoicePage />} />

        {/* Users */}
        <Route path={Paths.users} element={<UsersListPage />} />
        <Route path={Paths.addUser} element={<AddEditUserPage />} />
        <Route path={Paths.editUser} element={<AddEditUserPage />} />

        {/* Roles */}
        <Route path={Paths.roles} element={<RolesListPage />} />
        <Route path={Paths.addRole} element={<AddEditRolePage />} />
        <Route path={Paths.editRole} element={<AddEditRolePage />} />

        {/* Audit Trail (admin-only) */}
        <Route path={Paths.auditLogs} element={<AuditLogsListPage />} />

        {/* Depots */}
        <Route path={Paths.depots} element={<DepotsListPage />} />
        <Route path={Paths.addDepot} element={<AddEditDepotPage />} />
        <Route path={Paths.editDepot} element={<AddEditDepotPage />} />

        {/* WMS - Companies */}
        <Route path={Paths.companies} element={<CompaniesListPage />} />
        <Route path={Paths.addCompany} element={<AddEditCompanyPage />} />
        <Route path={Paths.editCompany} element={<AddEditCompanyPage />} />

        {/* WMS - Warehouse Zones */}
        <Route path={Paths.zones} element={<ZonesListPage />} />
        <Route path={Paths.addZone} element={<AddEditZonePage />} />
        <Route path={Paths.editZone} element={<AddEditZonePage />} />

        {/* WMS - Warehouse Bins */}
        <Route path={Paths.bins} element={<BinsListPage />} />
        <Route path={Paths.addBin} element={<AddEditBinPage />} />
        <Route path={Paths.editBin} element={<AddEditBinPage />} />

        {/* WMS - Customers */}
        <Route path={Paths.customers} element={<CustomersListPage />} />
        <Route path={Paths.addCustomer} element={<AddEditCustomerPage />} />
        <Route path={Paths.editCustomer} element={<AddEditCustomerPage />} />

        {/* WMS - Suppliers */}
        <Route path={Paths.suppliers} element={<SuppliersListPage />} />
        <Route path={Paths.addSupplier} element={<AddEditSupplierPage />} />
        <Route path={Paths.editSupplier} element={<AddEditSupplierPage />} />


        {/* WMS - Number Series */}
        <Route path={Paths.numberSeries} element={<NumberSeriesListPage />} />
        <Route path={Paths.addNumberSeries} element={<AddEditNumberSeriesPage />} />
        <Route path={Paths.editNumberSeries} element={<AddEditNumberSeriesPage />} />

        {/* WMS - Configurations */}
        <Route path={Paths.configurations} element={<ConfigurationsListPage />} />
        <Route path={Paths.addConfiguration} element={<AddEditConfigurationPage />} />
        <Route path={Paths.editConfiguration} element={<AddEditConfigurationPage />} />
        <Route path={Paths.minInvoiceValues} element={<MinInvoiceValuesPage />} />
        <Route path={Paths.storeBrandRestrictions} element={<StoreBrandRestrictionsPage />} />
        <Route path={Paths.storeBlockList} element={<StoreBlockListPage />} />
        <Route path={Paths.sapLogs} element={<SapLogsListPage />} />
        <Route path={Paths.announcements} element={<AnnouncementsListPage />} />

        {/* WMS - Purchase Orders */}
        <Route path={Paths.purchaseOrders} element={<PurchaseOrdersListPage />} />
        <Route path={Paths.createPO} element={<CreateEditPOPage />} />
        <Route path={Paths.editPO} element={<CreateEditPOPage />} />
        <Route path={Paths.viewPO} element={<POViewPage />} />

        {/* WMS - GRN */}
        <Route path={Paths.grnList} element={<GRNListPage />} />
        <Route path={Paths.createGRN} element={<CreateGRNPage />} />
        <Route path={Paths.viewGRN} element={<GRNViewPage />} />
        <Route path={Paths.stoReceipt} element={<StoReceiptPage />} />
        <Route path={Paths.poReceipt} element={<PoReceiptPage />} />

        {/* WMS - Batches */}
        <Route path={Paths.batches} element={<BatchesListPage />} />

        {/* WMS - Stock Management */}
        <Route path={Paths.stockOnHand} element={<StockOnHandPage />} />
        <Route path={Paths.stockMovements} element={<StockMovementsPage />} />
        <Route path={Paths.stockAdjustment} element={<StockAdjustmentPage />} />
        <Route path={Paths.openingStock} element={<OpeningStockPage />} />
        <Route path={Paths.stockRegister} element={<StockRegisterPage />} />
        <Route path={Paths.salesRegister} element={<SalesRegisterPage />} />
        <Route path={Paths.purchaseRegister} element={<PurchaseRegisterPage />} />
        <Route path={Paths.productWiseSalesReport} element={<ProductWiseSalesReportPage />} />

        {/* Phase 2 - Sales Orders */}
        <Route path={Paths.salesOrders} element={<SalesOrdersListPage />} />
        <Route path={Paths.createSO} element={<CreateEditSOPage />} />
        <Route path={Paths.editSO} element={<CreateEditSOPage />} />
        <Route path={Paths.viewSO} element={<SOViewPage />} />

        {/* Phase 2 - Picking */}
        <Route path={Paths.pickLists} element={<PickListsListPage />} />
        <Route path={Paths.createPickList} element={<CreatePickListPage />} />
        <Route path={Paths.viewPickList} element={<PickListViewPage />} />

        {/* Phase 2 - Dispatch */}
        <Route path={Paths.dispatches} element={<DispatchListPage />} />
        <Route path={Paths.createDispatch} element={<CreateDispatchPage />} />
        <Route path={Paths.viewDispatch} element={<DispatchViewPage />} />

        {/* Phase 2 - WMS Invoices */}
        <Route path={Paths.wmsInvoices} element={<WmsInvoicesListPage />} />
        <Route path={Paths.viewWmsInvoice} element={<WmsInvoiceViewPage />} />

        {/* Phase 2 - Credit Notes */}
        <Route path={Paths.creditNotes} element={<CreditNotesListPage />} />
        <Route path={Paths.createCreditNote} element={<CreateCreditNotePage />} />
        <Route path={Paths.viewCreditNote} element={<CreditNoteViewPage />} />

        {/* Phase 2 - Debit Notes */}
        <Route path={Paths.debitNotes} element={<DebitNotesListPage />} />
        <Route path={Paths.createDebitNote} element={<CreateDebitNotePage />} />
        <Route path={Paths.viewDebitNote} element={<DebitNoteViewPage />} />

        {/* Phase 2 - Payments */}
        <Route path={Paths.payments} element={<PaymentsListPage />} />
        <Route path={Paths.createPayment} element={<CreatePaymentPage />} />
        <Route path={Paths.viewPayment} element={<PaymentViewPage />} />

        {/* Phase 2 - Sales Returns */}
        <Route path={Paths.salesReturns} element={<SalesReturnsListPage />} />
        <Route path={Paths.createSalesReturn} element={<CreateSalesReturnPage />} />
        <Route path={Paths.viewSalesReturn} element={<SalesReturnViewPage />} />

        {/* Depo Returns (walk-in / POS-originated invoice returns) */}
        <Route path={Paths.depoReturns} element={<DepoReturnsListPage />} />
        <Route path={Paths.createDepoReturn} element={<CreateEditDepoReturnPage />} />
        <Route path={Paths.editDepoReturn} element={<CreateEditDepoReturnPage />} />
        <Route path={Paths.viewDepoReturn} element={<DepoReturnViewPage />} />
        <Route path={Paths.posReturnSOsList} element={<PosReturnSOsListPage />} />
        <Route path={Paths.posReturnSOImport} element={<PosReturnSOImportPage />} />

        {/* Phase 3 - Putaway */}
        <Route path={Paths.putawayTasks} element={<PutawayTasksListPage />} />
        <Route path={Paths.viewPutaway} element={<PutawayViewPage />} />

        {/* Phase 3 - Shelf Transfers */}
        <Route path={Paths.shelfTransfers} element={<ShelfTransfersListPage />} />
        <Route path={Paths.createShelfTransfer} element={<CreateShelfTransferPage />} />
        <Route path={Paths.viewShelfTransfer} element={<ShelfTransferViewPage />} />

        {/* Phase 3 - Cycle Counts */}
        <Route path={Paths.cycleCounts} element={<CycleCountsListPage />} />
        <Route path={Paths.createCycleCount} element={<CreateCycleCountPage />} />
        <Route path={Paths.viewCycleCount} element={<CycleCountViewPage />} />

        {/* Purchase Requests */}
        <Route path={Paths.purchaseRequests} element={<PurchaseRequestsListPage />} />
        <Route path={Paths.createPR} element={<CreatePurchaseRequestPage />} />
        <Route path={Paths.editPR} element={<EditPurchaseRequestPage />} />
        <Route path={Paths.viewPR} element={<PRViewPage />} />
        <Route path={Paths.depotPRMappings} element={<DepotPRMappingsPage />} />

        {/* Inter-Depot Purchase Orders */}
        <Route path={Paths.interDepotPOs} element={<InterDepotPOsListPage />} />
        <Route path={Paths.createPOFromPR} element={<CreatePOFromPRPage />} />
        <Route path={Paths.editInterDepotPO} element={<CreatePOFromPRPage />} />
        <Route path={Paths.viewInterDepotPO} element={<InterDepotPOViewPage />} />

        {/* Phase 3 - Stock Transfers */}
        <Route path={Paths.stockTransfers} element={<StockTransfersListPage />} />
        <Route path={Paths.createStockTransfer} element={<CreateStockTransferPage />} />
        <Route path={Paths.viewStockTransfer} element={<StockTransferViewPage />} />
        <Route path={Paths.receiveStockTransfer} element={<StockTransferReceivePage />} />

        {/* Phase 3 - Stock Levels & Expiry */}
        <Route path={Paths.stockLevels} element={<StockLevelsPage />} />
        <Route path={Paths.stockAlerts} element={<StockAlertsPage />} />
        <Route path={Paths.expiryDashboard} element={<ExpiryDashboardPage />} />
        <Route path={Paths.expirySettings} element={<ExpirySettingsPage />} />

        {/* Retail — Sale Bills */}
        <Route path={Paths.saleBills} element={<SaleBillsListPage />} />
        <Route path={Paths.createSaleBill} element={<CreateSaleBillPage />} />
        <Route path={Paths.createSaleBillPos} element={<CreateSaleBillPosPage />} />
        <Route path={Paths.viewSaleBill} element={<ViewSaleBillPage />} />

        {/* Retail — Sale Returns */}
        <Route path={Paths.saleReturns} element={<SaleReturnsListPage />} />
        <Route path={Paths.createSaleReturn} element={<CreateSaleReturnPage />} />
        <Route path={Paths.viewSaleReturn} element={<ViewSaleReturnPage />} />

        {/* Catch-all: redirect to dashboard instead of showing 404 */}
        <Route path="*" element={<Navigate to={Paths.dashboard} replace />} />
      </Routes>
    </Suspense>
  );
}
